import type { EngineEvent } from '@casino/craps-engine';
import type { SessionRecorder } from '../sessionRecorder.js';

/**
 * Producer-side debug overlay. Subscribes to the craps engine and shows
 * each event as it's emitted (which is also what the session recorder
 * forwards to the DO). Polls the recorder's WS state every 500ms so we
 * can see hello-handshake completion and buffer behavior.
 *
 * Used in tandem with the spectator-side overlay to triage end-to-end
 * gaps: events visible here but missing on the spectator → DO / WS
 * path is dropping frames. Events missing here → engine isn't firing
 * them (or the bridge teardown ran).
 *
 * Temporary debugging aid. Bottom-right, monospace, tap to dismiss.
 */

interface EngineLike {
  subscribe: (l: (e: EngineEvent) => void) => () => void;
}

export function mountPlayerDebugOverlay(recorder: SessionRecorder, engine: EngineLike): () => void {
  // Gated behind ?debug in the URL (same flag as the spectator overlay and the
  // craps hit-zone strokes). No-op cleanup when off so the call site is unchanged.
  if (typeof window === 'undefined' || !new URLSearchParams(window.location.search).has('debug')) {
    return () => {};
  }

  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; top: 8px; right: 8px;
    background: rgba(0,0,0,0.88); color: #ffb;
    font-family: monospace; font-size: 10px;
    padding: 6px 8px; border-radius: 6px;
    z-index: 99999; max-width: 280px; min-width: 220px;
    white-space: pre; line-height: 1.35;
    pointer-events: auto; cursor: pointer;
    overflow: hidden;
  `;
  el.title = 'Tap to hide';
  document.body.appendChild(el);

  const MAX_LINES = 12;
  const lines: string[] = [];
  let emittedCount = 0;

  const render = () => {
    const d = recorder.debug();
    const sid = d.streamCode ? d.streamCode.slice(0, 8) + '…' : '—';
    const header = [
      `ws:${d.wsState} hello:${d.helloDone}`,
      `buf:${d.bufferLength}  seq:${d.liveSeq}`,
      `code:${sid}  emitted:${emittedCount}`,
    ].join('\n');
    el.textContent = [header, '─'.repeat(28), ...lines.slice(-MAX_LINES)].join('\n');
  };

  const onClick = () => { unsubscribe(); clearInterval(timer); el.remove(); };
  el.addEventListener('click', onClick);

  let lastEmitAt = 0;
  // mm:ss.fff so consecutive engine emissions show up as +Nms deltas.
  const ts = (): string => {
    const d = new Date();
    return `${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}.${String(d.getMilliseconds()).padStart(3, '0')}`;
  };

  const unsubscribe = engine.subscribe((event) => {
    emittedCount += 1;
    const now = performance.now();
    const delta = lastEmitAt === 0 ? 0 : Math.round(now - lastEmitAt);
    lastEmitAt = now;
    let detail = '';
    if (event.type === 'BetPlaced') {
      const b = event.bet;
      detail = ` ${b.type}${b.number !== undefined ? '/' + b.number : ''} $${(b.amount / 100).toFixed(0)}`;
    } else if (event.type === 'BankrollChanged') {
      detail = ` $${(event.bankroll / 100).toFixed(0)}`;
    } else if (event.type === 'PointEstablished') {
      detail = ` ${event.point}`;
    } else if (event.type === 'RollResolved') {
      detail = ` ${event.roll.d1}+${event.roll.d2}=${event.roll.total}`;
    }
    // The recorder subscribes before this overlay (registered earlier in
    // createCrapsBridge), so by the time we run, the recorder has already
    // processed this event. If it was sent, lastSeq is the seq just used.
    // If it was buffered (WS down or hello pending), lastSeq is the last
    // *confirmed* seq — the current event will get lastSeq+1 once it
    // flushes. Suffix with a '?' in the buffered case so the read is clear.
    const d = recorder.debug();
    const seqStr = d.helloDone && d.wsState === 'open' ? `#${d.liveSeq}` : `#${d.liveSeq + 1}?`;
    lines.push(`${ts()} +${delta}ms ${seqStr} ${event.type}${detail}`);
    while (lines.length > MAX_LINES * 2) lines.shift();
    render();
  });

  render();
  const timer = setInterval(render, 500);

  return () => { unsubscribe(); clearInterval(timer); el.remove(); };
}
